import type { DataGridDataRowTemplateProps, RowTemplateType } from '@myosh/odin-components';
import type { MutableRefObject, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Module } from '../../@types/modules';
import useDoubleClick from '../../hooks/use-double-click';
import { forceAssert } from '../../utils/type-guards';
import type { ActiveRecordRef } from '../active-record/active-record.component';
import OdinLoadingIndicator from '../common/odin-loading-indicator.component';
import { RecordTitle } from '../record/record-title.component';
import { constructRecordTitle } from '../record/util/record-component-util';

export const LinkedRecordRowProps = {
  id: 'id',
  moduleId: 'moduleId',
  formName: 'formName',
  docNo: 'docNo',
  status: 'status',
  date: 'lastModifiedDate',
  vikingLinkUrl: 'vikingLinkUrl',
} as const;
export type LinkedRecordRowProps = (typeof LinkedRecordRowProps)[keyof typeof LinkedRecordRowProps];

export interface OdinDataGridRowTemplateProps {
  modules?: Module[];
  activeRecordReference?: MutableRefObject<ActiveRecordRef | null | undefined>;
  recordFields?: Record<string, string>;
  isClickable?: boolean;
  listOfPermittedRecordIds?: string[];
  pendingLinkedRecords?: string[];
  openerRecordId?: string; // ID of the record that opened this linked record (usually the parent record)
}

export function OdinDataGridRowTemplate({
  modules,
  activeRecordReference,
  recordFields = LinkedRecordRowProps,
  isClickable = true,
  listOfPermittedRecordIds,
  pendingLinkedRecords = [],
  openerRecordId,
}: OdinDataGridRowTemplateProps) {
  const { rowClickHandler } = useDoubleClick();
  const { t } = useTranslation();

  const rowTemplate: RowTemplateType = (
    props: DataGridDataRowTemplateProps,
    children: ReactNode | Array<ReactNode>
  ) => {
    const module = modules?.find(
      (item: unknown) => forceAssert<{ id: number }>(item).id === props.data[recordFields?.moduleId]
    );

    const recordId = String(props.data.id);
    const hasPermissionsToRead = listOfPermittedRecordIds?.includes(recordId);
    const isPending = pendingLinkedRecords.includes(recordId);

    if (module && isClickable && hasPermissionsToRead) {
      const date = new Date(props.data[recordFields.date as string] as string | Date);
      const title = returnRecordTitle(props, recordFields);
      const details = <RecordTitle title={title} date={date} />;

      const handleClick = (isDoubleClick: boolean) => {
        const shouldOpenFullScreen = isDoubleClick || (activeRecordReference?.current?.isFullScreen?.() ?? false);
        const recordId = props.data[recordFields.id];
        activeRecordReference?.current?.addActiveRecord(String(recordId), module.icon, details, shouldOpenFullScreen, {
          title,
          recordId: recordId,
          viewId: module.viewId,
          moduleIcon: module.icon,
          formId: props.data.formId,
          formName: props.data[recordFields.formName as string],
          docNo: props.data[recordFields.docNo as string],
          status: props.data[recordFields.status as string],
          vikingLinkUrl: recordFields.vikingLinkUrl ? props.data[recordFields.vikingLinkUrl as string] : undefined,
          displayText: recordFields.displayText ? props.data[recordFields.displayText as string] : undefined,
          openerRecordId,
        });
      };

      const handleRowClick = () => {
        rowClickHandler(handleClick);
      };

      return (
        <div className="flex w-full cursor-pointer" onClick={handleRowClick}>
          {children}
        </div>
      );
    } else if (isPending) {
      return (
        <div className="flex w-full items-center justify-center">
          <div className="flex items-center justify-center">
            <OdinLoadingIndicator />
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex w-full items-center justify-center">
          <div className="flex items-center justify-center">
            <span className="font-medium text-primary-1">{t('no-record-permissions')}</span>
          </div>
        </div>
      );
    }
  };

  return rowTemplate;
}

export const returnRecordTitle = (props: DataGridDataRowTemplateProps, recordFields: Record<string, unknown>) => {
  const formName = String(props.data[recordFields.formName as string]);
  const docNo = String(props.data[recordFields.docNo as string]);
  const status = String(props.data[recordFields.status as string]);

  return constructRecordTitle(formName, docNo, status);
};
